import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormControl, Validators } from '@angular/forms';
import { CollectionsMode } from '@anduin/config';
import { distinctUntilObjectChanged, getControlValueChanges } from '@anduin/shared';
import { Store } from '@ngxs/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, takeUntil, tap } from 'rxjs/operators';
import { StatementRecipientLabel } from '../../models';
import { CommonCollectionsState } from '../../state';

export interface ThrottlingState {
  checked: boolean;
  days: number;
}

@Component({
  selector: 'app-collections-throttling',
  templateUrl: './collections-throttling.component.html',
  styleUrls: ['./collections-throttling.component.scss'],
  standalone: false,
})
export class CollectionsThrottlingComponent implements OnInit, OnDestroy {
  readonly defaultDays: number = 14;

  private _disabled = false;

  @Input()
  set throttlingState(throttlingState: ThrottlingState) {
    // revert state if provided days value is invalid
    this.daysControl.setValue(
      `${!throttlingState?.days || !Number.isInteger(throttlingState.days) ? this.defaultDays : throttlingState.days}`,
      { emitEvent: false },
    );
    this.checkControl.setValue(throttlingState.checked, { emitEvent: false });

    // enable number of days input only if checkbox is checked and not disabled
    if (!this._disabled) {
      const action: 'enable' | ('disable' & keyof AbstractControl) = throttlingState?.checked ? 'enable' : 'disable';
      this.daysControl[action]();
    }
  }

  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.checkControl.disable();
      this.daysControl.disable();
    } else {
      this.checkControl.enable();
      // Only enable days control if checkbox is checked
      if (this.checkControl.value) {
        this.daysControl.enable();
      }
    }
  }

  get disabled(): boolean {
    return this._disabled;
  }

  @Output() throttlingStateChange = new EventEmitter<ThrottlingState>();

  @Output() isValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  checkControl: UntypedFormControl = new UntypedFormControl(true);
  daysControl = new UntypedFormControl(null, {
    validators: [Validators.required, Validators.min(1), Validators.maxLength(3)],
    updateOn: 'change',
  });

  // for number of days input
  errorMessage?: string;

  private readonly _unsubscribe$ = new Subject<void>();

  /**
   * If invalid, get error message for number of days input.
   * Otherwise, emit throttling state on blur.
   */
  onDaysControlValueChanges$ = getControlValueChanges<`${number}`>(this.daysControl, true).pipe(
    distinctUntilChanged(),
    tap((_) => (this.errorMessage = this.getErrorMessage())),
    takeUntil(this._unsubscribe$),
  );

  statementRecipientLabel$: Observable<StatementRecipientLabel> = this.store
    .select(CommonCollectionsState.getCollectionsMode)
    .pipe(map((mode) => (mode === CollectionsMode.CONTACT ? StatementRecipientLabel.CONTACT : StatementRecipientLabel.CLIENT)));

  constructor(private readonly store: Store) {
    this.onDaysControlValueChanges$.subscribe();
  }

  ngOnInit() {
    this.daysControl.statusChanges
      .pipe(
        distinctUntilChanged(),
        tap((status) => this.isValid.emit(status !== 'INVALID')),
        takeUntil(this._unsubscribe$),
      )
      .subscribe();

    // Whenever a values updates, emit the new state
    combineLatest([
      getControlValueChanges<boolean>(this.checkControl),
      getControlValueChanges<`${number}`>(this.daysControl).pipe(map(Number)),
    ])
      .pipe(
        skip(1),
        map(([checked, days]) => ({ checked, days })),
        filter((_) => !this.daysControl.errors),
        distinctUntilObjectChanged(),
        tap((state) => this.throttlingStateChange.emit(state)),
        takeUntil(this._unsubscribe$),
      )
      .subscribe();
  }

  /**
   * Checkbox change callback. Update input form control and emit throttling state. Disable input if unchecked.
   * @param checked
   */
  onCheckboxChange(checked: boolean): void {
    this.daysControl.setValidators(checked ? [Validators.required, Validators.min(1), Validators.maxLength(3)] : []);

    // Don't enable if the component is disabled
    if (!this._disabled) {
      const action: 'enable' | ('disable' & keyof AbstractControl) = checked ? 'enable' : 'disable';
      this.daysControl[action]();
    }
  }

  /**
   * Get error message for number of days input based on it's validity and error state.
   */
  getErrorMessage(): string {
    if (this.daysControl.valid || this.daysControl.disabled) {
      return '';
    }

    if ('required' in this.daysControl.errors) {
      return 'The number of days is required.';
    } else if ('min' in this.daysControl.errors) {
      return 'Number of days must be greater than zero.';
    } else {
      return 'Number of days cannot be longer than three digits.';
    }
  }

  markAllAsTouched(): void {
    this.daysControl.markAllAsTouched();
  }

  reset(throttlingState: ThrottlingState): void {
    this.throttlingState = throttlingState;
  }

  /**
   * Clean up subscriptions.
   */
  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
